import { useState } from "react";

import { getCsrfToken } from "../lib/api";

const ACCESS_TOKEN_KEY = "solidariteit.accessToken";

const writeAccessToken = (token?: string | null) => {
  if (token) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  }
};
const readAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);

export type Body = Record<string, Record<string, unknown>>;

export type FailureHandler = (opts: {
  body?: Body;
  json?: any;
  response?: Response;
}) => void;
export type SuccessHandler<D = any> = (opts: {
  json: D;
  response: Response;
}) => void;

const useFetch = <D>(
  url: string,
  opts?: {
    method?: string;
    onFailure?: FailureHandler;
    onSuccess?: SuccessHandler<D>;
  },
): [
  fetch: (body?: Body) => void,
  { data?: D; error?: string; loading: boolean; submitted: boolean },
] => {
  const [data, setData] = useState(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { method, onFailure, onSuccess } = opts ?? {};

  const fetchData = (body?: Body) => {
    setLoading(true);
    setError(undefined);
    setSubmitted(false);
    const token = readAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (method) {
      headers["X-CSRF-Token"] = getCsrfToken();
      headers["Content-Type"] = "application/json";
    }

    fetch(url, {
      body: JSON.stringify(body),
      method,
      headers,
    })
      .then(async (response) => {
        writeAccessToken(response.headers.get("X-Authorization-Token"));

        const json = await response.json().catch(() => undefined);
        setData(json);

        if (!response.ok) {
          setError(
            "Er ging iets fout. Probeer het opnieuw of neem contact op.",
          );

          onFailure?.({ body, json, response });
        } else {
          setSubmitted(true);
          onSuccess?.({ json, response });
        }
      })
      .catch(() => {
        setError("Netwerkfout. Controleer je verbinding.");

        onFailure?.({});
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return [fetchData, { data, loading, error, submitted }];
};

export default useFetch;
